import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Sparkles, ChevronDown, Loader2, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

const INDUSTRIES = ["Technology", "Finance", "Healthcare", "Fashion", "Manufacturing", "Real Estate", "Hospitality", "Education", "Marketing", "Legal", "Consulting", "Media", "Wellness", "Retail", "Other"];
const CHAPTERS = ["Hyderabad (Founding)", "Bengaluru (Coming Soon)", "Chennai (Coming Soon)", "Mumbai (Coming Soon)", "Delhi NCR (Coming Soon)", "Other / Online Member"];

const BENEFITS = [
  { c: "Networking", t: "Access to all WOWSHE networking chapters" },
  { c: "Referrals", t: "Submit, request and track business referrals" },
  { c: "Workshops", t: "36 empowerment workshops across 2026" },
  { c: "Learning", t: "All online courses & masterclasses included" },
  { c: "Wellness", t: "12 health & wellness programs annually" },
  { c: "Leadership", t: "Mentor circle + chapter leadership roles" },
  { c: "Directory", t: "Verified public profile in the member directory" },
  { c: "Recognition", t: "Founding member badge — lifetime rate locked" },
];

const FAQS = [
  { q: "Who can apply for membership?", a: "Any woman 21+ who is an entrepreneur, professional, freelancer, or aspiring leader. Based in Hyderabad ideal; women from other cities welcome — full chapter access launches as we expand." },
  { q: "When does my membership begin?", a: "Immediately after successful payment. Annual validity runs 365 days from activation." },
  { q: "How does renewal work?", a: "We email a renewal reminder 30 days before expiry. Founding-member pricing is locked for life if you renew continuously." },
  { q: "What's the refund policy?", a: "Memberships are non-refundable mid-year. You can choose not to renew at the end of the cycle." },
  { q: "How is payment processed?", a: "Securely via Razorpay — supports UPI, cards, net-banking, and wallets. Receipts are emailed automatically." },
  { q: "What if my chapter hasn't launched yet?", a: "You get full access to online programs, the directory, the community channel, and priority membership in your chapter when it launches in 2026." },
];

export default function Membership() {
  const { user, register: registerUser } = useAuth();
  const nav = useNavigate();
  const [openFaq, setOpenFaq] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", mobile: "", password: "",
    city: "Hyderabad", profession: "", business: "", industry: INDUSTRIES[0],
    chapter: CHAPTERS[0], terms: false,
  });

  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.terms) { toast.error("Please accept the Terms to continue."); return; }
    setBusy(true);
    try {
      // 1. Register new user (creates pending application) OR use existing approved user
      let activeUser = user;
      if (!activeUser) {
        activeUser = await registerUser({
          name: form.name, email: form.email, password: form.password,
          phone: form.mobile, city: form.city, industry: form.industry,
          business: form.business,
        });
        await api.put("/auth/profile", { chapter: form.chapter });
        toast.success("Application received! Awaiting admin approval.");
        nav("/pending");
        return;
      }

      // 2. Existing approved user — open Razorpay to upgrade
      if (activeUser.approval_status && activeUser.approval_status !== "approved") {
        toast("Your application is still under review.");
        nav("/pending");
        return;
      }

      const cfg = await api.get("/payments/config").then(r => r.data);
      if (!cfg.configured) {
        toast.error("Razorpay sandbox keys not configured. Ask admin to add RAZORPAY_KEY_ID & SECRET.");
        setBusy(false);
        return;
      }

      const order = await api.post("/payments/create-order", { kind: "membership" }).then(r => r.data);

      const options = {
        key: order.key_id, amount: order.amount, currency: order.currency,
        order_id: order.order_id, name: "WOWSHE", description: "Annual Membership · Founding Cohort",
        image: "/wowshe-logo.png",
        prefill: { name: activeUser.name, email: activeUser.email, contact: activeUser.phone || form.mobile },
        theme: { color: "#3A0F4D" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment verified · Welcome to WOWSHE Annual!");
            nav("/dashboard");
          } catch (err) {
            toast.error(formatErr(err.response?.data?.detail) || "Verification failed.");
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Couldn't submit application.");
      setBusy(false);
    }
  };

  return (
    <div data-testid="membership-page">
      {/* Hero */}
      <section className="section-pad container-luxe">
        <div className="text-center max-w-3xl mx-auto">
          <div className="overline justify-center flex items-center gap-2"><Sparkles className="w-3 h-3" /> Annual Membership</div>
          <h1 className="font-display text-5xl md:text-6xl text-aubergine mt-6 leading-tight">
            One annual fee. <em className="text-rose not-italic">A community for life.</em>
          </h1>
          <p className="mt-5 text-lg text-plumSoft font-light">
            Founding-cohort pricing · Locked for life if you renew continuously.
          </p>
        </div>
      </section>

      {/* Pricing + Form side by side */}
      <section className="container-luxe pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* LEFT — Plan card */}
          <div className="lg:col-span-5">
            <div className="card-luxe overflow-hidden sticky top-28">
              <div className="bg-aubergine text-ivory p-8 relative overflow-hidden">
                <div className="absolute -right-12 -top-12 w-44 h-44 bg-champagne/10 rounded-full blur-3xl" />
                <p className="overline !text-champagne">Founding Plan</p>
                <h2 className="font-display text-4xl mt-2">WOWSHE Annual</h2>
                <div className="mt-6 flex items-end gap-3">
                  <p className="font-display text-5xl">₹ 10,000</p>
                  <span className="text-xs uppercase tracking-widest text-champagne mb-2">/ year + GST</span>
                </div>
                <p className="mt-3 text-xs text-ivory/60">Valid for 365 days from activation</p>
              </div>
              <div className="p-7">
                <p className="overline mb-4">What's included</p>
                <ul className="space-y-2.5">
                  {BENEFITS.map((b) => (
                    <li key={b.c} className="flex items-start gap-3 text-sm">
                      <Check className="w-4 h-4 text-rose flex-none mt-0.5" />
                      <span><strong className="text-aubergine font-medium">{b.c}:</strong> <span className="text-plumSoft">{b.t}</span></span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-4 border-t border-aubergine/10 flex items-center gap-2 text-xs text-plumSoft">
                  <Shield className="w-3.5 h-3.5 text-teal" />
                  Secure checkout via Razorpay · UPI · Cards · Net-banking
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-7 card-luxe p-8 md:p-10">
            <p className="overline">Join the founding cohort</p>
            <h2 className="font-display text-3xl text-aubergine mt-2">Your details</h2>
            <p className="text-sm text-plumSoft mt-1 font-light">
              {user ? `You're signed in as ${user.email}. Confirm details and proceed.` : "Takes under a minute. We review applications within 5 working days."}
            </p>

            <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full Name" v={form.name} k="name" upd={upd} required testId="m-name" disabled={!!user && !!user.name} default={user?.name} />
              <Field label="Email" v={form.email} k="email" upd={upd} required type="email" testId="m-email" disabled={!!user} default={user?.email} />
              <Field label="Mobile" v={form.mobile} k="mobile" upd={upd} required testId="m-mobile" default={user?.phone} />
              <Field label="City" v={form.city} k="city" upd={upd} required testId="m-city" default={user?.city || "Hyderabad"} />
              <Field label="Profession" v={form.profession} k="profession" upd={upd} required testId="m-prof" />
              <Field label="Business Name" v={form.business} k="business" upd={upd} testId="m-biz" default={user?.business} />
              <div>
                <label className="label-luxe">Industry</label>
                <select data-testid="m-industry" value={form.industry} onChange={(e) => upd("industry", e.target.value)} className="input-luxe">
                  {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label className="label-luxe">Chapter Preference</label>
                <select data-testid="m-chapter" value={form.chapter} onChange={(e) => upd("chapter", e.target.value)} className="input-luxe">
                  {CHAPTERS.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              {!user && (
                <div className="md:col-span-2">
                  <label className="label-luxe">Create a Password</label>
                  <input data-testid="m-password" required minLength={6} type="password" value={form.password} onChange={(e) => upd("password", e.target.value)} className="input-luxe" />
                </div>
              )}
            </div>

            <label className="mt-7 flex items-start gap-3 cursor-pointer">
              <input data-testid="m-terms" type="checkbox" checked={form.terms} onChange={(e) => upd("terms", e.target.checked)} className="mt-1 accent-rose w-4 h-4" />
              <span className="text-sm text-plumSoft font-light">
                I agree to WOWSHE's <Link to="#" className="text-plum link-underline">Member Code of Conduct</Link>,{" "}
                <Link to="#" className="text-plum link-underline">Terms of Service</Link> and <Link to="#" className="text-plum link-underline">Privacy Policy</Link>.
              </span>
            </label>

            <button data-testid="m-pay" disabled={busy} className="btn-primary mt-7 w-full md:w-auto md:min-w-[280px] justify-center">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing…</> : (user ? "Pay ₹10,000 & Activate" : "Submit Application")}
            </button>
            <p className="mt-3 text-xs text-plumSoft/70">
              {user
                ? "You'll be redirected to Razorpay's secure checkout. Test mode is active — use a test card or UPI."
                : "Your application is reviewed within 5 working days. Payment & dashboard access unlock after approval."}
            </p>
          </form>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad bg-lavender/60">
        <div className="container-luxe max-w-3xl">
          <p className="overline">Frequently asked</p>
          <h2 className="font-display text-4xl md:text-5xl text-aubergine mt-3">Membership questions</h2>
          <div className="mt-10 space-y-3">
            {FAQS.map((f, i) => (
              <div key={i} data-testid={`faq-${i}`} className="card-luxe overflow-hidden">
                <button type="button" onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between text-left p-5 md:p-6">
                  <span className="font-display text-lg text-aubergine">{f.q}</span>
                  <ChevronDown className={`w-5 h-5 text-plum transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <div className="px-5 md:px-6 pb-5 md:pb-6 text-plumSoft font-light leading-relaxed">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, v, k, upd, required, type = "text", testId, disabled, default: dflt }) {
  const val = dflt && !v ? dflt : v;
  return (
    <div>
      <label className="label-luxe">{label}{required && " *"}</label>
      <input
        data-testid={testId}
        type={type}
        required={required}
        value={val}
        disabled={disabled}
        onChange={(e) => upd(k, e.target.value)}
        className={`input-luxe ${disabled ? "opacity-60" : ""}`}
      />
    </div>
  );
}
